import { motion } from "framer-motion";
import { Phone, MessageCircle, Mail, Navigation, MapPin, Smartphone } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { LINKS } from "../lib/assets";
import { Reveal, MaskedLine, GoldLine, Eyebrow } from "../components/Reveal";

export default function Contact() {
  const { t } = useLang();
  const c = t.contact;

  const actions = [
    { id: "call", label: t.common.callSchool, value: "044 2486 7105", href: LINKS.tel, Icon: Phone, external: false },
    { id: "whatsapp", label: t.common.whatsapp, value: "+91 76048 38880", href: LINKS.whatsapp, Icon: MessageCircle, external: true },
    { id: "email", label: t.common.emailUs, value: "sathyaschool1989@gmail.com", href: LINKS.email, Icon: Mail, external: false },
    { id: "directions", label: t.common.directions, value: "Valasaravakkam, Chennai", href: LINKS.directions, Icon: Navigation, external: true },
  ];

  return (
    <>
      <section className="bg-ivory pb-16 pt-36 lg:pt-44">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <MaskedLine>
            <h1 className="font-heading text-5xl font-semibold leading-none text-navy sm:text-6xl lg:text-7xl">{c.title}</h1>
          </MaskedLine>
          <GoldLine className="mt-8 w-20" delay={0.3} />
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-2xl text-base font-light leading-relaxed text-ink/70 md:text-lg"
          >
            {c.sub}
          </motion.p>
        </div>
      </section>

      <section className="bg-ivory pb-24" data-testid="contact-actions">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <Eyebrow>{c.reachEyebrow}</Eyebrow>
          </Reveal>
          <div className="mt-8 grid gap-px bg-gold/30 sm:grid-cols-2 lg:grid-cols-4">
            {actions.map((a, i) => (
              <Reveal key={a.id} delay={i * 0.1}>
                <a
                  href={a.href}
                  target={a.external ? "_blank" : undefined}
                  rel={a.external ? "noopener noreferrer" : undefined}
                  data-testid={`contact-action-${a.id}`}
                  className="group flex h-full flex-col bg-cream p-8 transition-colors duration-500 hover:bg-navy lg:p-10"
                >
                  <a.Icon size={26} strokeWidth={1.4} className="text-gold" />
                  <span className="mt-6 font-heading text-2xl font-semibold text-navy transition-colors duration-500 group-hover:text-ivory">
                    {a.label}
                  </span>
                  <span className="mt-3 block h-px w-8 bg-gold transition-[width] duration-500 group-hover:w-16" aria-hidden="true" />
                  <span className="mt-4 break-words text-sm font-light text-ink/70 transition-colors duration-500 group-hover:text-ivory/80">
                    {a.value}
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-navy-deep py-24 text-ivory lg:py-28" data-testid="contact-details">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12">
          <Reveal className="min-w-0 lg:col-span-5">
            <Eyebrow light>{c.addressTitle}</Eyebrow>
            <h2 className="mt-6 font-heading text-3xl font-semibold leading-snug md:text-4xl">{t.common.schoolName}</h2>
            <p className="mt-6 flex items-start gap-3 text-base font-light leading-relaxed text-ivory/80">
              <MapPin size={18} className="mt-1 shrink-0 text-gold" />
              <span>
                {t.common.addressLines[0]}
                <br />
                {t.common.addressLines[1]}
              </span>
            </p>
            <div className="mt-8 space-y-4 text-base text-ivory/80">
              <a href={LINKS.tel} data-testid="contact-landline" className="flex items-center gap-3 transition-colors duration-300 hover:text-gold">
                <Phone size={17} className="text-gold" />
                <span>
                  <span className="mr-2 text-xs uppercase tracking-[0.2em] text-ivory/50">{t.common.landline}</span>
                  044 2486 7105
                </span>
              </a>
              <a href={LINKS.mobile} data-testid="contact-mobile" className="flex items-center gap-3 transition-colors duration-300 hover:text-gold">
                <Smartphone size={17} className="text-gold" />
                <span>
                  <span className="mr-2 text-xs uppercase tracking-[0.2em] text-ivory/50">{t.common.mobile}</span>
                  +91 76048 38880
                </span>
              </a>
              <a href={LINKS.email} data-testid="contact-email" className="flex items-center gap-3 transition-colors duration-300 hover:text-gold">
                <Mail size={17} className="text-gold" />
                <span>
                  <span className="mr-2 text-xs uppercase tracking-[0.2em] text-ivory/50">{t.common.email}</span>
                  sathyaschool1989@gmail.com
                </span>
              </a>
            </div>
            <p className="mt-8 text-sm font-light text-ivory/60">{c.officeNote}</p>
          </Reveal>

          <Reveal delay={0.15} className="min-w-0 lg:col-span-7">
            <Eyebrow light>{c.mapEyebrow}</Eyebrow>
            <h3 className="mt-6 font-heading text-2xl font-semibold md:text-3xl">{c.mapTitle}</h3>
            <div className="relative mt-6">
              <span className="absolute -left-3 -top-3 h-full w-full border border-gold/40" aria-hidden="true" />
              <iframe
                title="SMHSS location map"
                data-testid="contact-map"
                src={LINKS.mapEmbed}
                className="relative h-[380px] w-full border-0 sm:h-[440px]"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
